/** Shared building blocks for every Settings section.
 *
 *  Extracted from SystemSettings so panels that live in their own files
 *  (notification preferences, policy, device push) can use the same idiom
 *  rather than inventing a second one. The rules these encode:
 *
 *  - No section renders its own title. The sidebar and window toolbar already
 *    show it; a heading in the body is the same word a third time.
 *  - Rows are text-forward. The icon is optional and earns its place only
 *    beside an action, never as decoration on a read-only fact.
 *  - Dividers belong to the Group, so a trailing hairline cannot hang.
 *  - Nothing in a section body goes above 14px. */
import React from 'react';
import { ChevronRight } from '@/shared/ui/icons';

const SingleColumnContext = React.createContext(false);

/** Holds every SettingsColumns inside it to one column, for settings shown in a dialog rather than a full page. */
export function SettingsSingleColumn({ children }: { children: React.ReactNode }) {
  return <SingleColumnContext.Provider value>{children}</SingleColumnContext.Provider>;
}

/** Two-column settings body.
 *
 *  A single 560px column leaves most of a desktop window empty, but simply
 *  widening it pulls a row's right-hand value away from its label until they
 *  stop reading as a pair. So the measure stays fixed and the SECOND column
 *  takes the slack. Groups are grid items, so a group can never be split
 *  across the fold.
 *
 *  Collapses to one column below 1024px, the project's desktop cut, and inside
 *  a SettingsSingleColumn, whose pane is narrower than the viewport says. */
export function SettingsColumns({ children, fullWidth = false, singleColumn = false }: { children: React.ReactNode; fullWidth?: boolean; singleColumn?: boolean }) {
  const forcedSingle = React.useContext(SingleColumnContext);
  return (
    <div className={`grid w-full grid-cols-1 items-start gap-x-12 ${singleColumn || forcedSingle ? '' : 'lg:grid-cols-2'} ${fullWidth ? '' : 'max-w-[1180px]'}`}>
      {children}
    </div>
  );
}

/** Sub-title strip at the top of a settings section: the one line of context
 *  the section needs, plus an optional section-level action.
 *
 *  It deliberately does NOT render the section's name. The window chrome and
 *  the sidebar both already show it (SETTINGS_SECTION_TITLES -> getAppViewTitle),
 *  so a heading here was the same word a third time. The icon tile went with
 *  it: a coloured glyph next to a title that isn't there is pure decoration.
 *  Sections with nothing useful to say simply omit this. */
export function SectionIntro({
  description,
  action,
}: {
  description?: string | React.ReactNode;
  action?: React.ReactNode;
}) {
  if (!description && !action) return null;
  return (
    <div className="flex items-center justify-between gap-3 pb-3 mb-1 border-b border-border">
      {description ? (
        <p
          style={{
            fontFamily: 'var(--font-apple)',
            fontSize: 'var(--text-label)',
            color: 'var(--muted-foreground)',
          }}
        >
          {description}
        </p>
      ) : <span />}
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function GroupLabel({ label }: { label: string }) {
  return (
    <p
      className="mt-5 mb-1 first:mt-2"
      style={{
        fontFamily: 'var(--font-apple)',
        fontSize: 'var(--settings-section-label-size, 10px)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--muted-foreground)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}
    >
      {label}
    </p>
  );
}

/** A labelled run of Rows. Owns the dividers at the container, so the last row
 *  in a group can never leave a hanging hairline no matter what the call site
 *  passes — the old way relied on remembering `noDivider` on the final Row.
 *  The last child must BE the Row (don't wrap a trailing Row in a fragment). */
export function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <GroupLabel label={label} />
      <div className="[&>*:last-child]:border-b-0">{children}</div>
    </div>
  );
}

export function Row({
  icon: Icon,
  iconColor,
  iconBg,
  label,
  description,
  value,
  action,
  danger = false,
  badge,
  noDivider = false,
  onClick,
  chevron = false,
}: {
  /** Optional. A coloured chip earns its place next to an ACTION the user can
   *  take; against a read-only fact it is decoration, and a column of them
   *  turns a settings list into a colour chart. Omit it and the row lays out
   *  as plain text. */
  icon?: React.ElementType;
  iconColor?: string;
  iconBg?: string;
  label: string;
  description?: string;
  value?: string;
  action?: React.ReactNode;
  danger?: boolean;
  badge?: React.ReactNode;
  noDivider?: boolean;
  /** Makes the whole row the hit target. Only rows that actually go somewhere
   *  should take this — a hover fill on a read-only fact reads as a broken
   *  link. */
  onClick?: () => void;
  /** Disclosure chevron after `action`. Default false so the existing
   *  Settings/Notifications/Devices rows are unaffected — only a row that
   *  navigates to another screen (Profile's panels) should set this. */
  chevron?: boolean;
}) {
  // Literal hover fill, not var(--muted): the macOS shell remaps --muted to
  // var(--mac-control-bg-hover, var(--muted)) on the SAME element as :root, so
  // the fallback is self-referential and resolves only because Swift injects
  // the --mac-* value. A must-render fill shouldn't depend on that.
  const interactive = !!onClick;
  const Tag = interactive ? 'button' : 'div';
  return (
    <Tag
      data-settings-row
      {...(interactive ? { type: 'button' as const, onClick } : {})}
      className={[
        'flex items-center gap-3 py-2 w-full text-left',
        !noDivider ? 'border-b border-border' : '',
        interactive
          ? 'cursor-pointer transition-colors hover:bg-[rgba(118,118,128,0.12)] dark:hover:bg-[rgba(118,118,128,0.18)]'
          : '',
      ].filter(Boolean).join(' ')}
    >
      {Icon && (
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: iconBg || 'var(--muted)',
            color: iconColor || 'var(--muted-foreground)',
          }}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            style={{
              fontFamily: 'var(--font-apple)',
              fontSize: 'var(--text-menu)',
              fontWeight: 'var(--font-weight-medium)',
              color: danger ? 'var(--status-danger)' : 'var(--foreground)',
            }}
          >
            {label}
          </span>
          {badge}
        </div>
        {description && (
          <p
            style={{
              fontFamily: 'var(--font-apple)',
              fontSize: 'var(--text-label)',
              color: 'var(--muted-foreground)',
            }}
          >
            {description}
          </p>
        )}
        {value && (
          <p
            style={{
              fontFamily: 'var(--font-apple)',
              fontSize: 'var(--text-label)',
              color: danger ? 'var(--status-danger)' : 'var(--muted-foreground)',
            }}
          >
            {value}
          </p>
        )}
      </div>

      {action && <div data-settings-action className="shrink-0 ml-2">{action}</div>}
      {chevron && <ChevronRight className="w-4 h-4 shrink-0 ml-1 text-muted-foreground opacity-60" />}
    </Tag>
  );
}

export function Pill({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded-full"
      style={{
        fontFamily: 'var(--font-apple)',
        fontSize: '10px',
        fontWeight: 'var(--font-weight-semibold)',
        color,
        background: bg,
        letterSpacing: '0.02em',
      }}
    >
      {label}
    </span>
  );
}
